import * as React from 'react'
import { FC, useMemo, useState } from 'react';
import { AppointmentData } from '../../models';
import { DateRangeSelectorState } from '../../components/DateRangeSelector';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { AutocompleteTextField, DateField } from '../../components/Forms/fields';
import { Alert } from 'reactstrap';
import { ResponsiveBar } from '@nivo/bar';
import Typography from '../../components/Typography';
import { CSVLink } from 'react-csv';
import { useDispatch } from 'react-redux';
import { openClient } from '../../store/entities/actions';
import AppButton from '../../components/AppButton';
import { serviceName } from '../../shared/appointmint/appointment';
import { useGeneralSettings } from '../../store/settings/hooks';


type ProfessionalName = string
type ClientName = string

const getRetentionData = (
    appointments: AppointmentData[],
    window: DateRangeSelectorState,
    splitDate: string,
    filterValues: string[],
    typeToName: (type: string) => string,
): Record<ProfessionalName, Record<ClientName, {clientId: string, retained: boolean, appointmentType: string}>> => {

    const filter = (a: AppointmentData) => !filterValues.length || !filterValues.some(v => ![
        a.clientName ?? '',
        a.professionalName ?? '',
        typeToName(a.type),
        a.room,
    ].includes(v))

    const lastClientAppointmentBeforeSplit = appointments
        .filter(a => filter(a) && a.date <= splitDate)
        .reduce<Record<ClientName, AppointmentData>>((ret, a) => ({
            ...ret,
            [a.clientName ?? '']: (ret[a.clientName ?? '']?.date ?? '0000-00-00') < a.date ? a : ret[a.clientName ?? ''],
        }), {})
    const clientsWithAppointmentsAfterSplit = new Set(appointments.filter(a => filter(a) && a.date > splitDate).map(a => a.clientName ?? ''))
    const professionalClientsRetention = Object.entries(lastClientAppointmentBeforeSplit)
        .reduce<Record<ProfessionalName, Record<ClientName, {clientId: string, retained: boolean, appointmentType: string}>>>((ret, [clientName, {professionalName = '', clientId, type}]) => ({
            ...ret,
            [professionalName]: {
                ...(ret[professionalName] ?? {}),
                [clientName]: {
                    clientId: clientId ?? 'unknown',
                    appointmentType: typeToName(type),
                    retained: clientsWithAppointmentsAfterSplit.has(clientName),
                },
            },
        }), {})

    return professionalClientsRetention
}

export const AnalyticsRetention: FC<{
    appointments: AppointmentData[],
    window: DateRangeSelectorState,
}> = ({
    appointments,
    window,
}) => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const generalSettings = useGeneralSettings()

    const [retentionSplitDate, setRetentionSplitDate] = useState<string>(new Date().toISOString().substring(0, 10))
    const [filter, setFilter] = useState<string[]>([])

    const formatValue = (value: string|number) => {
        if (typeof value !== 'number') return value
        return `${Math.round(value*1000) / 10} %`
    }

    const possibleFilterValues = useMemo<string[]>(() => [...new Set([
        ...appointments.map(a => serviceName(a.type, generalSettings)),
        ...appointments.map(a => a.room),
        ...appointments.map(a => a.professionalName ?? ''),
        ...appointments.map(a => a.clientName ?? ''),
    ])], [appointments, generalSettings])

    const retentionDetails = getRetentionData(appointments, window, retentionSplitDate, filter, t => serviceName(t, generalSettings))

    const detailedData: Record<string, string|number>[] = [{
        date: retentionSplitDate,
        ...retentionDetails && Object.fromEntries(Object.entries(retentionDetails).map(([professionalName, clientsRetentionFlag]) => [
            professionalName,
            Object.values(clientsRetentionFlag).filter(v => v.retained).length / Object.values(clientsRetentionFlag).length,
        ])),
    }]

    const totals = detailedData.reduce<Record<string, number>>((acc, curr) => ({
        ...acc,
        ...Object.fromEntries(Object.entries(curr)
            .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
            .map(([k, v]) => [k, (acc[k] ?? 0) + v])),
    }), {})

    const totalsData = Object.entries(totals).map(([k, v]) => ({label: k, [t(TK.value)]: v}))

    const total = totalsData.map(e => e[t(TK.value)]).reduce<number>((total, value) =>
        total + (value as number), 0) / totalsData.length

    return (<>
        <Alert color='secondary' style={{marginTop: 'var(--spacer-S)'}}>{t(TK.retentionQuestion, window.from, retentionSplitDate, window.to)}</Alert>

        <div style={{display: 'flex', gap: 'var(--spacer-S)', flexDirection: 'column'}}>
            <DateField
                label={t(TK.retentionAnalysisSplitDate)}
                helpText={t(TK.retentionAnalysisDescription)}
                value={retentionSplitDate}
                type="date"
                onChange={setRetentionSplitDate}
            />

            <AutocompleteTextField
                label={t(TK.filter)}
                style={{flexGrow: 1}}
                options={possibleFilterValues}
                multiSelect
                values={filter}
                onValuesChanged={setFilter}
            />
        </div>

        <hr/>
        <h2 style={{textAlign: 'center'}}><b>{t(TK.total)}:</b> {formatValue(total)}</h2>
        <hr/>

        <div style={{height:`${150+totalsData.length*20}px`, width: '100%'}}>
            <ResponsiveBar
                data={totalsData.reverse()}
                keys={[t(TK.value)]}
                labelFormat={formatValue}
                tooltipFormat={formatValue}
                labelSkipWidth={35}
                labelSkipHeight={12}
                indexBy="label"
                margin={{
                    top: 10,
                    right: 10,
                    bottom: 50,
                    left: totalsData.reduce((curr, item) => curr > (item.label as string).length ? curr : (item.label as string).length, 0) * 5 + 15,
                }}
                padding={0.3}
                colors={{ scheme: 'nivo' }}
                layout="horizontal"
                axisBottom={{
                    legendOffset: 36,
                    legendPosition: 'middle',
                }}
                enableGridX={true}
            />
        </div>

        <hr/>

        <h3>{t(TK.detailed)}</h3>

        <Alert color='secondary' style={{marginTop: 'var(--spacer-S)'}}>{t(TK.retentionProfessionalMessage, retentionSplitDate)}</Alert>

        <div style={{marginTop: 'var(--spacer-S)'}}>
            {Object.entries(retentionDetails).map(([professional, clientsRetentionFlag]) => (<div key={professional}>
                <h5>{professional} ({formatValue(totals[professional])})</h5>
                <ul>
                    {Object.entries(clientsRetentionFlag).map(([client, {retained, clientId, appointmentType}]) =>
                        <li style={{color: retained ? 'var(--green)' : 'var(--danger)'}}>
                            <AppButton color='link' style={{padding: '0', verticalAlign: 'baseline'}} onClick={() => dispatch(openClient(clientId))}>{client}</AppButton>
                            &nbsp;-
                            &nbsp;<b>{retained ? t(TK.retained) : t(TK.left)}</b>
                            &nbsp;<Typography size='smaller'>({appointmentType})</Typography>
                        </li>
                    )}
                </ul>
            </div>))}
        </div>

        <CSVLink
            data={detailedData}
            separator=';'
            target='_blank'
            filename='analysis-export.csv'
        >
            {t(TK.exportData)}
        </CSVLink>
    </>)
}
